import React, { useState, useEffect } from 'react';
import { App, Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { formatDate } from '../utils/format';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  orders_count?: number;
}

const Customers: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadCustomers();
  }, [page, pageSize]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/customers', {
        params: {
          offset: (page - 1) * pageSize,
          limit: pageSize,
        },
      });
      setCustomers(response.data.customers || []);
      setTotal(response.data.count || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('customers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredData = debouncedSearch
    ? customers.filter(
        (c) =>
          `${c.first_name} ${c.last_name}`.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          c.email?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : customers;

  const columns = [
    {
      title: t('customers.firstName'),
      dataIndex: 'first_name',
      key: 'first_name',
      render: (name: string, record: Customer) =>
        name || (!record.last_name ? record.email || '-' : '-'),
    },
    {
      title: t('customers.lastName'),
      dataIndex: 'last_name',
      key: 'last_name',
    },
    {
      title: t('customers.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('customers.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('customers.title')}
        </h1>
        <Input
          placeholder={t('common.search')}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => navigate(`/customers/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default Customers;
